import {
  ButtonDS,
  InputDS,
  Spacer,
  TextCapitalized,
  ArrowForwardOutlined,
  hasEmailFormat,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useAppSelector } from "../../../../redux/hooks";
import styled from "styled-components";
import { selectManagersList } from "./../usersSlice";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { UserInformations } from "./addUserPage";
import "../../../../i18n/i18n";

export const AddUserInformations = (props: {
  onSubmit: (informations: UserInformations) => void;
  firstName: string;
  lastName: string;
  email: string;
  job: string;
}) => {
  const { t } = useTranslation();
  const { onSubmit } = props;
  const [displayError, setDisplayError] = useState<boolean>(false);
  const managers = useAppSelector(selectManagersList);

  const [firstName, setFirstName] = useState<string>(props.firstName ?? "");
  const [lastName, setLastName] = useState<string>(props.lastName ?? "");
  const [email, setEmail] = useState<string>(props.email ?? "");
  const [job, setJob] = useState<string>(props.job ?? "");

  const hasDuplicateEmail: boolean = useMemo(() => {
    if (!email) return false;
    return !!managers.find(
      (manager) => manager.email?.toUpperCase() === email?.toUpperCase(),
    );
  }, [email, managers]);

  const isValidForm: boolean = useMemo(
    () =>
      !!(
        firstName &&
        lastName &&
        email &&
        hasEmailFormat(email) &&
        !hasDuplicateEmail
      ),
    [email, firstName, hasDuplicateEmail, lastName],
  );

  const clickToSubmit = useCallback(() => {
    if (!isValidForm) {
      setDisplayError(true);
      return;
    }
    onSubmit({ firstName, lastName, email, job });
  }, [email, firstName, isValidForm, job, lastName, onSubmit, setDisplayError]);

  return (
    <StyledModalContainer>
      <StyledModalContent>
        <Spacer y={1} />
        <StyledTitle>
          <TextCapitalized>
            {t(`users.modal.informations.title`)}
          </TextCapitalized>
        </StyledTitle>
        <Spacer y={2} />
        <StyledFlex>
          <InputDS
            label={t("users.modal.firstName") || ""}
            placeholder={t("users.modal.firstName.placeholder")}
            error={
              displayError && !firstName
                ? t("users.modal.error.input")
                : undefined
            }
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
          />
          <InputDS
            label={t("users.modal.lastName") || ""}
            placeholder={t("users.modal.lastName.placeholder")}
            error={
              displayError && !lastName
                ? t("users.modal.error.input")
                : undefined
            }
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
          />
        </StyledFlex>
        <InputDS
          label={t("users.modal.email") || ""}
          placeholder={t("users.modal.email.placeholder")}
          error={
            displayError && !email
              ? t("users.modal.error.input")
              : displayError && !hasEmailFormat(email)
                ? t("users.modal.error.emailFormat")
                : hasDuplicateEmail
                  ? t("users.modal.error.duplicateEmail")
                  : undefined
          }
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
          isPlaceholderUppercase
        />
        <InputDS
          label={t("users.modal.job") || ""}
          placeholder={t("users.modal.job.placeholder")}
          value={job}
          onChange={(e) => setJob(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
        />
        <Spacer y={0.75} />
        <div>
          <ButtonDS
            rightIcon={<ArrowForwardOutlined />}
            text={t("users.modal.informations.submit")}
            format={"hug"}
            buttonType={"primary"}
            onClick={clickToSubmit}
            disabled={!isValidForm}
          />
        </div>
      </StyledModalContent>
    </StyledModalContainer>
  );
};

const StyledModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledModalContent = styled.div`
  flex-direction: column;
  display: flex;
  width: 33rem;
  align-items: center;
`;

const StyledFlex = styled.div`
  display: flex;
  width: 100%;
`;

const StyledTitle = styled.div`
  ${typographies["Header/H1"]}
`;
